import { Assets } from 'pixi.js'
import { GodziDropSoundTypeV2, soundManager, VoucherSoundType } from '@slot-nx/core'
import { GameSoundType } from '../sound/GameSoundType'
import { gameConfig } from '../../config'

const SOUND_BATCH_SIZE = 2
const SOUND_RETRY_COUNT = 2
const SOUND_RETRY_DELAY_MS = 300

export class AssetManager {
  async init(manifestUrl: string) {
    await Assets.init({ manifest: manifestUrl })
  }

  async loadEssential() {
    await Promise.all([
      Assets.load('texturePacker'),
      Assets.loadBundle(['images', 'locales', 'spine', 'fonts']),
    ])

    try {
      await Assets.loadBundle(['sounds'])
      soundManager.loadFromAssetPack(Object.values(GameSoundType), { customStorageKey: gameConfig.name })
    }
    catch (err) {
      console.error('[AssetManager] Sounds bundle load failed, user try load by batches', err)
      await this.loadEssential_sound_fallback()
    }
  }

  private async loadEssential_sound_fallback() {
    const loaded = await this.loadSoundsInBatches(Object.values(GameSoundType))
    try {
      soundManager.loadFromAssetPack(loaded, { customStorageKey: gameConfig.name })
    }
    catch (err) {
      console.error('[AssetManager] sound fallback load by batche init failed', err)
    }
  }

  private async loadSoundsInBatches(
    aliases: string[],
    batchSize = SOUND_BATCH_SIZE,
    retries = SOUND_RETRY_COUNT,
  ): Promise<string[]> {
    const loaded: string[] = []
    const failed: string[] = []

    for (let i = 0; i < aliases.length; i += batchSize) {
      const batch = aliases.slice(i, i + batchSize)
      await Promise.all(
        batch.map(async (alias) => {
          for (let attempt = 1; attempt <= retries + 1; attempt++) {
            try {
              await Assets.load(alias)
              loaded.push(alias)
              return
            }
            catch (err) {
              if (attempt > retries) {
                failed.push(alias)
                console.warn(`[AssetManager] Sound "${alias}" failed after ${retries + 1} attempts`, err)
              }
              else {
                await new Promise<void>(r => setTimeout(r, SOUND_RETRY_DELAY_MS * attempt))
              }
            }
          }
        }),
      )
    }

    if (failed.length > 0) {
      console.warn('[AssetManager] Sounds load skipped (non-blocking):', failed)
    }
    return loaded
  }

  async loadBackground() {
    await Promise.all([
      this.loadGodziDropSource(),
      this.loadVoucherSource(),
    ])
  }

  public async loadGodziDropSource() {
    await Assets.loadBundle(['godziDrop'])
    soundManager.loadSounds(Object.values(GodziDropSoundTypeV2))
  }

  public async loadVoucherSource() {
    await Assets.loadBundle(['voucher'])
    soundManager.loadSounds(Object.values(VoucherSoundType))
  }

  async loadOpbetDetailResource() {
    await Promise.all([
      Assets.load('texturePacker'),
      Assets.loadBundle(['locales', 'fonts']),
    ])
  }
}
